var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
import * as React from 'react';
import { rem } from 'polished';
import * as Immutable from 'seamless-immutable';
import { connect } from 'react-redux';
import { request } from '@esri/arcgis-rest-request';
import ConfigManager from './config-manager';
import { PageType, DialogMode, ContainerType } from './types/app-config';
import * as actions from './app-actions';
import { getModuleSync } from './module-loader';
import { getAppIdPageIdFromUrl, getDialogIdFromIdOrLabel, getSectionNavInfoFromQueryObject } from './utils/url-utils';
import { getPortalSelfInfoUrl } from './utils/portal-url-utils';
import { getJimuThemeModule, getParentWindow, isDeepEqual, showApp } from './utils/utils';
import AppKeyboardComponent from './app-keyboard-component';
import * as semver from 'semver';
import WidgetManager from './widget-manager';
import { injectIntl, FormattedMessage } from 'react-intl';
import DataSourceManager from './data-source-manager';
import defaultI18nMessages from './translations/default';
import { AppMode } from './types/common';
import SessionManager from './session-manager';
import { getAppStore } from './store';
import { SetDocumentTitle } from './set-document-title';
import { SetDocumentFavicon } from './set-document-favicon';
import { PermissionBannerList } from './permission-banner-list';
import { OAuthErrorPage } from './oauth-error-page';
import { AddMetaTag } from './add-meta-tag';
import { css, jsx } from '@emotion/react';
import { TrackingManager } from './tracking-manager';
import { LayoutItemType } from './types/layout';
import { focusElementInKeyboardMode } from './accessibility';
import { SetCanonical } from './set-canonical';
import { setConnectionInfoToAppConfig } from './utils/app-config-utils';
const currentVersion = require('../version.json');
const codeVersion = currentVersion.exbVersion;
class _AppRootComponent extends React.PureComponent {
    constructor(props) {
        super(props);
        this.getKeyboardComponent = () => {
            if (window.jimuConfig.isBuilder) {
                const BuilderKeyboardComponent = getModuleSync('jimu-for-builder').BuilderKeyboardComponent;
                return jsx(BuilderKeyboardComponent, null);
            }
            else if (window.jimuConfig.isInBuilder) {
                return jsx(AppKeyboardComponent, null);
            }
            else {
                return null;
            }
        };
        this.renderCookieBanner = (themeVariables) => {
            if (window.jimuConfig.isBuilder || window.jimuConfig.isSite) {
                return null;
            }
            const { CookieBanner } = this.getJimuUI();
            if (!CookieBanner) {
                return null;
            }
            return jsx(CookieBanner, { theme: themeVariables });
        };
        this.renderVersionRemind = () => {
            const { isVersionAlertOpened } = this.state;
            const { appConfigVersion } = this.props;
            const { Alert } = this.getJimuUI();
            const appState = getAppStore().getState();
            const appInfo = appState === null || appState === void 0 ? void 0 : appState.appInfo;
            const isHigherVersionApp = semver.lt(codeVersion, appConfigVersion);
            if (window.jimuConfig.isBuilder || !isHigherVersionApp || !isVersionAlertOpened) {
                return null;
            }
            const isTemplate = (appInfo === null || appInfo === void 0 ? void 0 : appInfo.type) === 'Web Experience Template';
            return (jsx(Alert, { className: 'w-100 position-absolute version-remind', open: isVersionAlertOpened, onClose: this.onVersionRemindClose, closable: true, style: {
                    zIndex: 2
                } }, !isTemplate
                ? this.props.intl.formatMessage({ id: 'experienceVersionRemind', defaultMessage: defaultI18nMessages.experienceVersionRemind })
                : this.props.intl.formatMessage({ id: 'templateVersionRemind', defaultMessage: defaultI18nMessages.templateVersionRemind })));
        };
        this.renderGuideRenderer = () => {
            var _a;
            const GuideRenderer = (_a = getModuleSync('jimu-ui/basic/guide')) === null || _a === void 0 ? void 0 : _a.GuideRenderer;
            if (!GuideRenderer) {
                return null;
            }
            return jsx(GuideRenderer, null);
        };
        this.onVersionRemindClose = () => {
            this.setState({
                isVersionAlertOpened: false
            });
        };
        this.onGoToAnotherExperienceBtnClick = () => {
            location.href = '/';
        };
        this.onSwitchAccountBtnClick = () => {
            SessionManager.getInstance().switchAccount();
        };
        this.onSignInBtnClick = () => {
            var _a, _b;
            const sessionManager = SessionManager.getInstance();
            sessionManager.signIn({ fromUrl: (_a = window.location) === null || _a === void 0 ? void 0 : _a.href, popup: false, desUrl: (_b = getAppStore().getState()) === null || _b === void 0 ? void 0 : _b.portalUrl, forceLogin: true });
        };
        this.getPortalVersion = (portalUrl) => {
            return request(getPortalSelfInfoUrl(portalUrl), {
                httpMethod: 'GET'
            }).then(portalSelf => Number(portalSelf === null || portalSelf === void 0 ? void 0 : portalSelf.currentVersion)).catch(err => {
                console.warn(err);
                return null;
            });
        };
        this.setClientId = (portalUrl) => __awaiter(this, void 0, void 0, function* () {
            const clientId = this.clientIdInput.current.value;
            const sessionManager = SessionManager.getInstance();
            const portalVersion = yield this.getPortalVersion(portalUrl);
            setConnectionInfoToAppConfig(portalUrl, clientId, portalVersion);
            getAppStore().dispatch(actions.removeFromRegisterClientIdList(portalUrl));
            sessionManager.onClientIdDialogFinished(portalUrl, clientId);
        });
        this.closeSetClientIdAlert = (portalUrl) => {
            const builderAppSync = getModuleSync('jimu-for-builder').builderAppSync;
            builderAppSync.publishSetClientIdAlertIsCancelled(portalUrl);
            getAppStore().dispatch(actions.removeFromRegisterClientIdList(portalUrl));
            SessionManager.getInstance().onClientIdDialogFinished(portalUrl, null, new Error('setting clientId is cancelled.'));
        };
        this.onDismiss = () => {
            var _a;
            this.props.dispatch(actions.userLocaleChanged(false));
            if (window.jimuConfig.isBuilder && ((_a = window._appWindow) === null || _a === void 0 ? void 0 : _a._appStore)) {
                window._appWindow._appStore.dispatch({
                    type: 'SET_HAS_NEW_VERSION',
                    hasNew: false
                });
                window._appWindow._appStore.dispatch({
                    type: 'USER_LOCALE_CHANGED',
                    isChanged: false
                });
            }
        };
        this.reload = () => {
            window.location.reload();
        };
        const state = {};
        const pageId = this.getPageId();
        if (pageId) {
            state.pageStatus = Immutable({
                [pageId]: true
            });
        }
        else {
            state.pageStatus = Immutable({});
        }
        state.preloadWidgetsLoaded = false;
        state.isVersionAlertOpened = true;
        this.state = state;
        this.onDismiss = this.onDismiss.bind(this);
        this.reload = this.reload.bind(this);
        this.setClientId = this.setClientId.bind(this);
        this.clientIdInput = React.createRef();
        // Builder may access the DataSourceManager before the app gets loaded, so we'll init it earlier
        DataSourceManager.getInstance();
    }
    getLayoutEntryPath() {
        let layoutEntryPath;
        if (window.jimuConfig.isInBuilder) {
            layoutEntryPath = 'jimu-layouts/layout-builder';
        }
        else {
            layoutEntryPath = 'jimu-layouts/layout-runtime';
        }
        return layoutEntryPath;
    }
    getPageRenderClass() {
        if (getModuleSync(this.getLayoutEntryPath())) {
            return (getModuleSync(this.getLayoutEntryPath())).PageRenderer;
        }
        else {
            console.error('Please init jimu-layouts.');
            return null;
        }
    }
    getJimuUI() {
        if (getModuleSync('jimu-ui')) {
            return getModuleSync('jimu-ui');
        }
        else {
            console.error('Please init jimu-ui.');
            return null;
        }
    }
    componentDidMount() {
        ConfigManager.getInstance({ intl: this.props.intl }).loadAppConfig().then(appConfig => {
            this.props.dispatch(actions.appConfigLoaded(appConfig));
            if (window.jimuConfig.isInBuilder) {
                // publish app state change to builder ASAP
                getModuleSync('jimu-for-builder').appBuilderSync.publishAppStateChangeToBuilder();
            }
            return appConfig;
        }).then(appConfig => {
            const sectionNavInfos = getSectionNavInfoFromQueryObject(this.props.queryObject, appConfig);
            Object.keys(sectionNavInfos).forEach(sectionId => {
                this.props.dispatch(actions.sectionNavInfoChanged(sectionId, sectionNavInfos[sectionId]));
            });
            this.props.dispatch(actions.currentPageChanged(this.getPageId()));
            this.addA11yKeyboardEvent(appConfig, this.props.queryObject);
            return appConfig;
        }).then(appConfig => {
            this.loadPreloadWidgets(appConfig).then(() => {
                showApp();
                if (window.jimuConfig.isInBuilder) {
                    if (window.parent.isExpressBuilder) {
                        this.selectMapWidget(appConfig);
                    }
                    DataSourceManager.getInstance().createAllDataSourcesByTime();
                }
            }).catch(err => {
                console.error('Initializing theme manager failed.', err);
                this.props.dispatch(actions.loadAppConfigFailed({
                    message: (err === null || err === void 0 ? void 0 : err.message) || '',
                    type: (err === null || err === void 0 ? void 0 : err.name) === 'ArcGISAuthError' ? err.name : 'CommonError',
                    code: err === null || err === void 0 ? void 0 : err.code
                }));
            });
            return appConfig;
        }).then(appConfig => {
            var _a, _b;
            const isDraft = (((_a = this.props.queryObject) === null || _a === void 0 ? void 0 : _a.draft) === 'true') || (((_b = this.props.queryObject) === null || _b === void 0 ? void 0 : _b.draft) === '1');
            if (!window.jimuConfig.isInBuilder && appConfig.analytics && appConfig.analytics.enable && !isDraft) {
                TrackingManager.getInstance().initialize(appConfig === null || appConfig === void 0 ? void 0 : appConfig.analytics).then((trackingManager) => {
                    // The first time pv event needs to log manually
                    trackingManager.logPageView();
                });
            }
            return appConfig;
        }).catch(err => {
            console.error('Load app config error.', err);
            this.props.dispatch(actions.loadAppConfigFailed({
                message: (err === null || err === void 0 ? void 0 : err.message) || '',
                type: (err === null || err === void 0 ? void 0 : err.name) === 'ArcGISAuthError' ? err.name : 'CommonError',
                code: err === null || err === void 0 ? void 0 : err.code
            }));
        });
    }
    componentWillUnmount() {
        const link = document.getElementById('jimu-base-style');
        link && link.remove();
    }
    selectMapWidget(appConfig) {
        const pageId = this.getPageId();
        const pageJson = appConfig.pages[pageId];
        if (!pageJson) {
            return;
        }
        const layoutEntry = getModuleSync('jimu-layouts/layout-runtime');
        const mapWidgets = layoutEntry.searchUtils.getMapWidgets(appConfig, appConfig.mainSizeMode, ContainerType.Page, pageId);
        if (mapWidgets.length === 0 || mapWidgets[0].maps.length === 0) {
            return;
        }
        const mapWidgetId = mapWidgets[0].maps[0];
        if (!appConfig.widgets[mapWidgetId]) {
            return;
        }
        const layoutInfos = layoutEntry.searchUtils.getLayoutInfosHoldContent(appConfig, LayoutItemType.Widget, mapWidgetId, appConfig.mainSizeMode);
        if (layoutInfos.length === 0) {
            return;
        }
        this.props.dispatch(actions.selectionChanged(layoutInfos[0]));
    }
    loadPreloadWidgets(appConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const alwaysPreloadWidgets = [];
            const preloadWidgets = ((_a = appConfig.preloadWidgets) === null || _a === void 0 ? void 0 : _a.asMutable()) || [];
            Object.keys(appConfig.widgets).forEach(wId => {
                const widgetJson = appConfig.widgets[wId];
                if (alwaysPreloadWidgets.includes(widgetJson.uri) && !preloadWidgets.includes(widgetJson.uri)) {
                    preloadWidgets.push(widgetJson.uri);
                }
            });
            if (preloadWidgets.length === 0) {
                this.setState({ preloadWidgetsLoaded: true });
                return Promise.resolve();
            }
            return Promise.all(preloadWidgets.map((widgetUri) => __awaiter(this, void 0, void 0, function* () { return yield WidgetManager.getInstance().loadWidgetClassByUri(widgetUri); }))).then(() => {
                this.setState({ preloadWidgetsLoaded: true });
            });
        });
    }
    componentDidUpdate() {
        const pageId = this.getPageId();
        let pages = this.state.pageStatus;
        if (pageId && !pages[pageId]) {
            Object.keys(pages).forEach(pId => {
                pages = pages.set(pId, false);
            });
            this.setState({
                pageStatus: pages.set(pageId, true)
            });
        }
    }
    addA11yKeyboardEvent(appConfig, queryObject) {
        // skip all focusable DOMs inside widgets, and focus on right sidePanel directly in design mode.
        if (window.jimuConfig.isInBuilder) {
            const appNode = document.body;
            appNode.setAttribute('tabindex', '0');
            appNode.style.outlineOffset = '-2px';
            appNode.addEventListener('keydown', (e) => {
                if ((e.target.tagName === 'BODY' || e.target.classList.contains('app-dialog-modal')) &&
                    e.key === 'Tab' && !e.shiftKey &&
                    getAppStore().getState().appRuntimeInfo.appMode === AppMode.Design) {
                    e.preventDefault();
                    const firstNode = window.parent.document.querySelector('div.builder-dynamic-setting .jimu-btn');
                    focusElementInKeyboardMode(firstNode, true);
                }
            });
        }
        else {
            const parent = getParentWindow();
            // add tabindex=0 to body to pass focus to window for embedded case.
            if (!window.jimuConfig.isBuilder && // preview app
                window.location !== (parent === null || parent === void 0 ? void 0 : parent.location) && // embedded
                (queryObject === null || queryObject === void 0 ? void 0 : queryObject.disable_window_focus) === 'true' &&
                appConfig.dialogs) {
                const appNode = document.body;
                appNode.setAttribute('tabindex', '0');
                appNode.addEventListener('focus', (e) => {
                    if (e.target.tagName === 'BODY') {
                        appNode.setAttribute('tabindex', '-1'); // The tabindex is useless now, because user must close all windows to skip exb app embedded.
                        const dlgModals = document.querySelectorAll('.app-dialog-modal');
                        if (dlgModals.length) {
                            e.preventDefault();
                            focusElementInKeyboardMode(dlgModals[dlgModals.length - 1], true);
                        }
                    }
                });
            }
        }
    }
    getPageId() {
        if (!this.props.pages) {
            return null;
        }
        const urlInfo = getAppIdPageIdFromUrl();
        // urlInfo.pageId does not exist in pages when it's a new un-saved page and clicking on the Reload Frame item from contextmenu.
        if (urlInfo.pageId && this.props.pages[urlInfo.pageId]) {
            return urlInfo.pageId;
        }
        const defaultPageId = Object.keys(this.props.pages).find(pId => this.props.pages[pId].isDefault);
        return defaultPageId;
    }
    getDialogId() {
        if (!this.props.dialogJson) {
            return null;
        }
        if (!window.jimuConfig.isInBuilder && this.props.dialogJson.mode === DialogMode.Anchored) {
            return null;
        }
        // the dialog is opened by link
        if (window.jimuConfig.isInBuilder && this.props.dialogJson.mode === DialogMode.Anchored && this.props.currentDialogId && this.props.currentDialogId !== getDialogIdFromIdOrLabel(this.props.queryObject.dlg, getAppStore().getState().appConfig)) {
            return null;
        }
        return this.props.dialogJson.id;
    }
    render() {
        var _a;
        const { pageStatus } = this.state;
        const PageRenderClass = this.getPageRenderClass();
        const jimuUI = this.getJimuUI();
        if (this.props.loadAppConfigError) {
            showApp();
            const msg = (_a = this.props.loadAppConfigError) === null || _a === void 0 ? void 0 : _a.message;
            if (msg === 'notPublishError') {
                const msgHTML = this.props.intl.formatMessage({ id: 'notPublishError', defaultMessage: defaultI18nMessages.notPublishError });
                return jsx("div", { id: 'not-publish-error', style: {
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }, dangerouslySetInnerHTML: { __html: msgHTML } });
            }
            // Invalid app item errors are cached by 'init app info' in the init.tsx
            // All auth errors for loading the app-config are caught later by 'hasPrivilege'
        }
        if (this.props.queryObject.oauth_error_code) {
            showApp();
            return this.renderOAuthError();
        }
        if (!this.props.hasPrivilege) {
            showApp();
            return this.renderPrivilegeError();
        }
        if (!this.props.appConfigLoaded || !PageRenderClass || !this.state.preloadWidgetsLoaded || isDeepEqual(this.props.themeVariables, {})) {
            return null;
        }
        const pageId = this.getPageId();
        if (!pageId) {
            console.error('No page to render.');
            return null;
        }
        if (!this.props.pages[pageId]) {
            console.error('Invalid page id.', pageId);
            return null;
        }
        if (this.props.pages[pageId].type === PageType.Folder) {
            console.error('This is a folder.');
            return null;
        }
        if (this.props.pages[pageId].linkUrl) {
            window.location.assign(this.props.pages[pageId].linkUrl);
            return null;
        }
        const Loading = jimuUI.Loading;
        return (jsx(React.Fragment, null,
            jsx(SetDocumentTitle, null),
            jsx(SetDocumentFavicon, { themeVariables: this.props.themeVariables }),
            jsx(AddMetaTag, null),
            jsx(SetCanonical, null),
            this.renderReloadAlert(this.props.themeVariables),
            this.renderSetClientIdAlert(this.props.themeVariables),
            this.renderVersionRemind(),
            this.renderPermissionBannerList(this.props.themeVariables),
            this.renderGuideRenderer(),
            jsx(PageRenderClass, { pageStatus: pageStatus, dialogId: this.getDialogId() }),
            jsx("div", { id: 'jimu-overlays-container' }),
            this.getKeyboardComponent(),
            Loading && this.props.isBusy && jsx(Loading, { type: this.props.loadingType }),
            this.renderCookieBanner(this.props.themeVariables)));
    }
    renderOAuthError() {
        var _a, _b;
        return (jsx(OAuthErrorPage, { oauthErrorCode: (_a = this.props.queryObject) === null || _a === void 0 ? void 0 : _a.oauth_error_code, fromUrl: (_b = this.props.queryObject) === null || _b === void 0 ? void 0 : _b.fromUrl, theme: this.props.themeVariables, intl: this.props.intl }));
    }
    renderPrivilegeError() {
        const { Button } = this.getJimuUI();
        const color = window.jimuConfig.isBuilder || window.jimuConfig.isSite ? 'white' : 'black';
        const backgroundColor = window.jimuConfig.isBuilder || window.jimuConfig.isSite ? '#232323' : '#f0f0f0';
        // temporarily modified z-index of app to cover the js-api OAuth confirm popup, no problems because all subsequent pages are redirected.
        document.getElementById('app').style.zIndex = '800';
        const isInvalidToken = this.props.privilegeError === 'INVALID_TOKEN';
        const errorMessage = isInvalidToken ? this.props.intl.formatMessage({ id: 'signInErrorInvalidToken', defaultMessage: defaultI18nMessages.signInErrorInvalidToken }) : this.props.privilegeError;
        const buttonStyle = {
            padding: '0.25rem 1rem',
            fontSize: '14px',
            userSelect: 'none',
            lineHeight: 1.5,
            borderRadius: '2px',
            color: '#fff',
            background: '#007f94',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: '#007f94',
            marginTop: '20px',
            outline: 'none',
            minWidth: '241px',
            height: '49px',
            margin: '79px 35px',
            cursor: 'pointer'
        };
        const session = SessionManager.getInstance().getMainSession();
        return (jsx("div", { id: "privilegeError", style: { width: '100vw', height: '100vh', color: color, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: '20px', backgroundColor } },
            jsx("div", { id: "foo", dangerouslySetInnerHTML: { __html: errorMessage } }),
            jsx("div", { style: { display: 'flex' } },
                Button && !isInvalidToken && session && jsx(Button, { size: 'lg', style: buttonStyle, onClick: this.onGoToAnotherExperienceBtnClick }, this.props.intl.formatMessage({ id: 'goToAnotherExperienceBtn', defaultMessage: defaultI18nMessages.goToAnotherExperienceBtn })),
                !window.jimuConfig.isInPortal && !isInvalidToken && session && Button && jsx(Button, { size: 'lg', style: buttonStyle, onClick: this.onSwitchAccountBtnClick }, this.props.intl.formatMessage({ id: 'switchAccountBtn', defaultMessage: defaultI18nMessages.switchAccountBtn })),
                Button && isInvalidToken && jsx(Button, { size: 'lg', style: buttonStyle, onClick: this.onSignInBtnClick }, this.props.intl.formatMessage({ id: 'signIn', defaultMessage: 'Sign in' })))));
    }
    renderSetClientIdAlert(theme) {
        if (!window.jimuConfig.isBuilder) {
            return null;
        }
        const portalInfo = this.props.portalsNeedToRegisterClientId && this.props.portalsNeedToRegisterClientId[0];
        const portalUrl = portalInfo === null || portalInfo === void 0 ? void 0 : portalInfo.portalUrl;
        if (!portalUrl) {
            return null;
        }
        const { AlertPopup, TextInput } = this.getJimuUI();
        const setClientIdMessage1 = this.props.intl.formatMessage({ id: 'setClientIdMessage1', defaultMessage: defaultI18nMessages.setClientIdMessage1 }, { register: this.props.intl.formatMessage({ id: 'registerText', defaultMessage: defaultI18nMessages.registerText }) });
        return (jsx(AlertPopup, { isOpen: true, title: '', onClickOk: () => { this.setClientId(portalUrl); }, onClickClose: () => { this.closeSetClientIdAlert(portalUrl); } },
            jsx("div", { style: {
                    color: theme.ref.palette.black,
                    fontSize: rem(16)
                } },
                jsx("p", null, setClientIdMessage1),
                jsx("p", null,
                    jsx(FormattedMessage, { id: 'arcgisEnterpriseText', defaultMessage: defaultI18nMessages.arcgisEnterpriseText })),
                jsx("p", { style: { color: theme.ref.palette.neutral[900] } }, portalUrl),
                jsx("p", null,
                    jsx(FormattedMessage, { id: 'setClientIdMessage2', defaultMessage: defaultI18nMessages.setClientIdMessage2 })),
                jsx(TextInput, { css: css `.input-wrapper {background-color: var(--ref-palette-neutral-400)}`, ref: this.clientIdInput }))));
    }
    renderReloadAlert(theme) {
        if (window.jimuConfig.isInBuilder || (window.jimuConfig.isSite && this.getPageId() === 'landing')) {
            return null;
        }
        if (!this.props.userLocaleChanged) {
            return null;
        }
        const { Alert, Button } = this.getJimuUI();
        return (jsx(Alert, { form: 'basic', type: 'info', open: this.props.userLocaleChanged, onClose: this.onDismiss, className: 'pl-4 pr-4', closable: true, css: css `
          position: absolute;
          z-index: 999;
          width: 100% !important;
          padding-top: 11px;
          padding-bottom: 11px;
          font-size: 1rem;
        ` },
            jsx(FormattedMessage, { id: 'userLocaleChangeMsg', defaultMessage: defaultI18nMessages.userLocaleChangeMsg }),
            jsx(Button, { size: 'sm', type: 'primary', className: 'ml-4', onClick: this.reload, style: { minWidth: rem(78), height: rem(26), marginTop: rem(-2) } },
                jsx(FormattedMessage, { id: 'doUpdate', defaultMessage: defaultI18nMessages.doUpdate }))));
    }
    renderPermissionBannerList(theme) {
        if (window.jimuConfig.isBuilder || window.jimuConfig.isInBuilder || !theme) {
            return null;
        }
        return (jsx(PermissionBannerList, { noPermissionResourceChangedFlag: this.props.noPermissionResourceChangedFlag, theme: theme, intl: this.props.intl }));
    }
}
const mapStateToProps = (state) => {
    var _a;
    if (!state.appConfig) {
        return {
            appConfigLoaded: false,
            customTheme: null,
            pages: null,
            appPath: state.appPath,
            queryObject: state.queryObject,
            isRTL: state.appContext.isRTL,
            loadingType: null,
            isBusy: state.appRuntimeInfo.isBusy,
            dialogJson: null,
            currentDialogId: null,
            userLocaleChanged: false,
            hasPrivilege: state.hasPrivilege,
            privilegeError: state.privilegeError,
            portalsNeedToRegisterClientId: state.portalsNeedToRegisterClientId,
            noPermissionResourceChangedFlag: state.noPermissionResourceChangedFlag,
            loadAppConfigError: state.loadAppConfigError,
            appConfigVersion: null
        };
    }
    return {
        appConfigLoaded: true,
        customTheme: state.appConfig.customTheme,
        pages: state.appConfig.pages,
        appPath: state.appPath,
        queryObject: state.queryObject,
        isRTL: state.appContext.isRTL,
        isBusy: state.appRuntimeInfo.isBusy,
        loadingType: state.appRuntimeInfo.loadingType,
        dialogJson: state.appConfig.dialogs ? state.appConfig.dialogs[getDialogIdFromIdOrLabel(state.queryObject.dlg, state.appConfig)] : null,
        currentDialogId: state.appRuntimeInfo.currentDialogId,
        userLocaleChanged: state.userLocaleChanged,
        hasPrivilege: state.hasPrivilege,
        privilegeError: state.privilegeError,
        portalsNeedToRegisterClientId: state.portalsNeedToRegisterClientId,
        noPermissionResourceChangedFlag: state.noPermissionResourceChangedFlag,
        loadAppConfigError: null,
        appConfigVersion: (_a = state.appConfig) === null || _a === void 0 ? void 0 : _a.exbVersion
    };
};
const AppRootComponent = (props) => {
    var _a, _b;
    const theme = (_b = (_a = getJimuThemeModule()) === null || _a === void 0 ? void 0 : _a.useTheme) === null || _b === void 0 ? void 0 : _b.call(_a, false);
    return jsx(_AppRootComponent, Object.assign({}, props, { themeVariables: theme }));
};
export default injectIntl(connect(mapStateToProps)(AppRootComponent));
